import type { Coding, Patient } from '@medplum/fhirtypes';

export { POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM } from './system';

import { POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM } from './system';
import {
  CONSENT_PENDING_TAG,
  ELIGIBILITY_CHECK_FINISHED_TAG,
  ELIGIBILITY_CHECK_PAGE_1_TAG,
  ELIGIBILITY_CHECK_PAGE_2_TAG,
  ELIGIBILITY_CONFIRMATION_TAG,
  EPWORTH_SLEEPINESS_SCALE_TAG,
  PCP_REFERRAL_FINISHED_TAG,
  PCP_REFERRAL_MISSING_ESS_TAG,
  PCP_REFERRAL_MISSING_STOPBANG_TAG,
  PCP_REFERRAL_PENDING_ESS_TAG,
  PCP_REFERRAL_PENDING_FILLIN_TAG,
  PCP_REFERRAL_PENDING_STOPBANG_TAG,
  PCP_REFERRAL_PHASE_1_COMPLETE_TAG,
  PCP_REFERRAL_PHASE_2_COMPLETE_TAG,
  PCP_REFERRAL_PHASE_3_COMPLETE_TAG,
  replaceTag,
  SLEEP_HISTORY_TAG,
  STOP_BANG_TAG,
  TAG_SYSTEM,
  WAITING_TRIAGE_RESULT_TAG,
} from './tags';
export const POLARIS_SYSTEM_STATUS_TAG_SYSTEM = 'http://polarissleep.com/status-tags/system';

export const POLARIS_EVALUATION_TAG_SYSTEM = 'http://polarissleep.com/status-tags/cc-evaluation';

export const PATIENT_EVALUATION_STATUS = {
  approved: 'approved',
  denied: 'denied',
} as const;

export const POLARIS_TRIAGE_STATUS_TAG_SYSTEM = 'http://polarissleep.com/status-tags/triage';

export const PATIENT_SYSTEM_STATUS = {
  active: 'active',
  inactive: 'inactive',
  prospect: 'prospect',
  archived: 'archived',
} as const;

type PatientSystemStatusMappingType = typeof PATIENT_SYSTEM_STATUS;
export type PatientSystemStatus = PatientSystemStatusMappingType[keyof PatientSystemStatusMappingType];

export const POLARIS_REFERRAL_STATUS_TAG_SYSTEM = 'http://polarissleep.com/status-tags/referral-out';

export const INLAB_REFERRAL_MODULE_URI = 'http://polarissleep.com/guidance/inlab-referral';
export const PERFORMER_EXTENSION_URL = 'http://polarissleep.com/extensions/performer';

export const PATIENT_REFERRAL_STATUS = {
  na: 'n/a',
  readyToRefer: 'ready-to-refer',
  referred: 'referred',
} as const;

export type PatientReferralStatus = (typeof PATIENT_REFERRAL_STATUS)[keyof typeof PATIENT_REFERRAL_STATUS];

export const PATIENT_INSURANCE_ELIGIBILITY_STATUS = {
  eligible: 'eligible',
  ineligible: 'ineligible',
} as const;

export type PatientInsuranceEligibilityStatus =
  (typeof PATIENT_INSURANCE_ELIGIBILITY_STATUS)[keyof typeof PATIENT_INSURANCE_ELIGIBILITY_STATUS];

export const PATIENT_TRIAGE_STATUS = {
  intake: 'intake',
  intakeAppointment: 'intake-appointment',
  hsat: 'hsat',
  hsatApprovedCm: 'hsat-approved-cm',
  hsatApprovedNp: 'hsat-approved-np',
  hsatOrdered: 'hsat-ordered',
  hsatResults: 'hsat-results',
  diagnosis: 'diagnosis',
  treatmentSetup: 'treatment-setup',
  followUp30Day: 'follow-up-30-day',
  followUp60Day: 'follow-up-60-day',
  followUp90Day: 'follow-up-90-day',
  followUp150Day: 'follow-up-150-day',
  followUp180Day: 'follow-up-180-day',
  miscFollowUp: 'misc-follow-up',
  remoteMonitoring: 'remote-monitoring',
  annualFollowUp: 'annual-follow-up',
  nonAdherent: 'non-adherent',
  denied: 'denied',
} as const;

export type PatientTriageStatus = (typeof PATIENT_TRIAGE_STATUS)[keyof typeof PATIENT_TRIAGE_STATUS];

/**
 * Human-readable triage Status Title for each triage status code, aligned with
 * patient_status_types.csv ("Status Title" column). Codes whose CSV title embeds
 * a provider name use a `{provider}` placeholder resolved by getTriageStatusTitle.
 * Codes with no CSV row carry a best-guess humanized label.
 */
const TRIAGE_STATUS_TITLES: Record<PatientTriageStatus, string> = {
  [PATIENT_TRIAGE_STATUS.intake]: 'Intake',
  [PATIENT_TRIAGE_STATUS.intakeAppointment]: 'Intake - Appointment',
  [PATIENT_TRIAGE_STATUS.hsat]: 'HSAT',
  [PATIENT_TRIAGE_STATUS.hsatApprovedCm]: 'Approved by {provider} for HSAT',
  [PATIENT_TRIAGE_STATUS.hsatApprovedNp]: 'Order Placed by {provider}',
  [PATIENT_TRIAGE_STATUS.hsatOrdered]: 'HSAT Ordered by Patient',
  [PATIENT_TRIAGE_STATUS.hsatResults]: 'HSAT Results',
  [PATIENT_TRIAGE_STATUS.diagnosis]: 'Diagnosis',
  [PATIENT_TRIAGE_STATUS.treatmentSetup]: 'Treatment Setup',
  [PATIENT_TRIAGE_STATUS.followUp30Day]: 'Follow-up 30-day',
  [PATIENT_TRIAGE_STATUS.followUp60Day]: 'Follow-up 60-day',
  [PATIENT_TRIAGE_STATUS.followUp90Day]: 'Follow-up 90-day',
  [PATIENT_TRIAGE_STATUS.followUp150Day]: 'Follow-up 150-day',
  [PATIENT_TRIAGE_STATUS.followUp180Day]: 'Follow-up 180-day',
  [PATIENT_TRIAGE_STATUS.miscFollowUp]: 'Misc. Follow-up',
  [PATIENT_TRIAGE_STATUS.remoteMonitoring]: 'Remote Patient Monitoring',
  [PATIENT_TRIAGE_STATUS.annualFollowUp]: 'Annual Follow-up',
  [PATIENT_TRIAGE_STATUS.nonAdherent]: 'Non-Adherent',
  [PATIENT_TRIAGE_STATUS.denied]: 'Denied - Refer Out',
};

/** Generic fallbacks for provider-bearing titles when no provider name is available. */
const TRIAGE_STATUS_TITLES_NO_PROVIDER: Partial<Record<PatientTriageStatus, string>> = {
  [PATIENT_TRIAGE_STATUS.hsatApprovedCm]: 'Approved for HSAT',
  [PATIENT_TRIAGE_STATUS.hsatApprovedNp]: 'Order Placed',
};

/**
 * Resolves a triage status code to its display Status Title.
 * @param code - the triage status tag code (PATIENT_TRIAGE_STATUS value), or undefined
 * @param opts.providerName - evaluating provider name for provider-bearing titles
 * @returns the Status Title, or '-' when the code is undefined/unrecognized
 */
export function getTriageStatusTitle(code: string | undefined, opts?: { providerName?: string }): string {
  if (!code) {
    return '-';
  }
  const template = TRIAGE_STATUS_TITLES[code as PatientTriageStatus];
  if (template === undefined) {
    return '-';
  }
  if (template.includes('{provider}')) {
    const providerName = opts?.providerName?.trim();
    if (providerName) {
      return template.replace('{provider}', providerName);
    }
    return TRIAGE_STATUS_TITLES_NO_PROVIDER[code as PatientTriageStatus] ?? template;
  }
  return template;
}

/**
 * Care Plan v2 patient status category. Stored on its own tag system alongside the
 * legacy triage tag, which still carries Testing milestones for the queues until
 * SLE-679 moves them onto clinical resources. `ineligible` is never stored here: it
 * is derived from the insurance-eligibility tag (see getPatientStatusCategory).
 */
export const POLARIS_STATUS_CATEGORY_TAG_SYSTEM = 'http://polarissleep.com/status-tags/category';

export const PATIENT_STATUS_CATEGORY = {
  intake: 'intake',
  testing: 'testing',
  treatment: 'treatment',
  referOut: 'refer-out',
  ineligible: 'ineligible',
} as const;

export type PatientStatusCategory = (typeof PATIENT_STATUS_CATEGORY)[keyof typeof PATIENT_STATUS_CATEGORY];

export const PATIENT_STATUS_CATEGORY_TITLES: Record<PatientStatusCategory, string> = {
  [PATIENT_STATUS_CATEGORY.intake]: 'Intake',
  [PATIENT_STATUS_CATEGORY.testing]: 'Testing',
  [PATIENT_STATUS_CATEGORY.treatment]: 'Treatment',
  [PATIENT_STATUS_CATEGORY.referOut]: 'Refer Out',
  [PATIENT_STATUS_CATEGORY.ineligible]: 'Ineligible',
};

const TRIAGE_STATUS_CATEGORIES: Record<PatientTriageStatus, PatientStatusCategory> = {
  [PATIENT_TRIAGE_STATUS.intake]: PATIENT_STATUS_CATEGORY.intake,
  [PATIENT_TRIAGE_STATUS.intakeAppointment]: PATIENT_STATUS_CATEGORY.intake,
  [PATIENT_TRIAGE_STATUS.hsat]: PATIENT_STATUS_CATEGORY.testing,
  [PATIENT_TRIAGE_STATUS.hsatApprovedCm]: PATIENT_STATUS_CATEGORY.testing,
  [PATIENT_TRIAGE_STATUS.hsatApprovedNp]: PATIENT_STATUS_CATEGORY.testing,
  [PATIENT_TRIAGE_STATUS.hsatOrdered]: PATIENT_STATUS_CATEGORY.testing,
  [PATIENT_TRIAGE_STATUS.hsatResults]: PATIENT_STATUS_CATEGORY.testing,
  [PATIENT_TRIAGE_STATUS.diagnosis]: PATIENT_STATUS_CATEGORY.testing,
  [PATIENT_TRIAGE_STATUS.treatmentSetup]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.followUp30Day]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.followUp60Day]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.followUp90Day]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.followUp150Day]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.followUp180Day]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.miscFollowUp]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.remoteMonitoring]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.annualFollowUp]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.nonAdherent]: PATIENT_STATUS_CATEGORY.treatment,
  [PATIENT_TRIAGE_STATUS.denied]: PATIENT_STATUS_CATEGORY.referOut,
};

/**
 * Legacy triage code written when staff move a patient into a category by hand, so
 * the queues that still filter on the legacy tag pick the patient up. Ineligible has
 * none because it lives on the insurance-eligibility tag.
 */
export const STATUS_CATEGORY_ENTRY_TRIAGE_STATUS: Record<
  Exclude<PatientStatusCategory, 'ineligible'>,
  PatientTriageStatus
> = {
  [PATIENT_STATUS_CATEGORY.intake]: PATIENT_TRIAGE_STATUS.intake,
  [PATIENT_STATUS_CATEGORY.testing]: PATIENT_TRIAGE_STATUS.hsat,
  [PATIENT_STATUS_CATEGORY.treatment]: PATIENT_TRIAGE_STATUS.treatmentSetup,
  [PATIENT_STATUS_CATEGORY.referOut]: PATIENT_TRIAGE_STATUS.denied,
};

/** Maps a legacy triage code to its status category, or undefined when unrecognized. */
export function getStatusCategoryForTriageStatus(code: string | undefined): PatientStatusCategory | undefined {
  return code ? TRIAGE_STATUS_CATEGORIES[code as PatientTriageStatus] : undefined;
}

/**
 * Resolves a patient's status category. Insurance ineligibility wins over everything;
 * otherwise the category tag is used, falling back to the legacy triage tag for
 * patients written before the category tag existed.
 */
export function getPatientStatusCategory(patient: Patient): PatientStatusCategory | undefined {
  const tags = patient.meta?.tag ?? [];
  const eligibility = tags.find((t) => t.system === POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM)?.code;
  if (eligibility === PATIENT_INSURANCE_ELIGIBILITY_STATUS.ineligible) {
    return PATIENT_STATUS_CATEGORY.ineligible;
  }
  const category = tags.find((t) => t.system === POLARIS_STATUS_CATEGORY_TAG_SYSTEM)?.code;
  if (category && category in PATIENT_STATUS_CATEGORY_TITLES && category !== PATIENT_STATUS_CATEGORY.ineligible) {
    return category as PatientStatusCategory;
  }
  return getStatusCategoryForTriageStatus(tags.find((t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM)?.code);
}

/** Display title for a status category, or '-' when unset. */
export function getStatusCategoryTitle(category: PatientStatusCategory | undefined): string {
  return category ? PATIENT_STATUS_CATEGORY_TITLES[category] : '-';
}

/**
 * Replaces the legacy triage tag with `code` and the category tag with the category
 * that code belongs to. Every automatic triage write goes through this so the two
 * tags never disagree.
 */
export function replaceTriageStatusTags(tags: Coding[], code: PatientTriageStatus): Coding[] {
  return replaceTag(replaceTag(tags, { system: POLARIS_TRIAGE_STATUS_TAG_SYSTEM, code }), {
    system: POLARIS_STATUS_CATEGORY_TAG_SYSTEM,
    code: TRIAGE_STATUS_CATEGORIES[code],
  });
}

const INTAKE_FORM_PHASES: readonly (string | undefined)[] = [
  // Self-referral / eligibility check
  ELIGIBILITY_CONFIRMATION_TAG.code,
  ELIGIBILITY_CHECK_PAGE_1_TAG.code,
  ELIGIBILITY_CHECK_PAGE_2_TAG.code,
  STOP_BANG_TAG.code,
  EPWORTH_SLEEPINESS_SCALE_TAG.code,
  SLEEP_HISTORY_TAG.code,
  ELIGIBILITY_CHECK_FINISHED_TAG.code,
  // Onboarding
  CONSENT_PENDING_TAG.code,
  WAITING_TRIAGE_RESULT_TAG.code,
  // PCP referral
  PCP_REFERRAL_PHASE_1_COMPLETE_TAG.code,
  PCP_REFERRAL_PHASE_2_COMPLETE_TAG.code,
  PCP_REFERRAL_PHASE_3_COMPLETE_TAG.code,
  PCP_REFERRAL_PENDING_STOPBANG_TAG.code,
  PCP_REFERRAL_PENDING_ESS_TAG.code,
  PCP_REFERRAL_PENDING_FILLIN_TAG.code,
  PCP_REFERRAL_MISSING_STOPBANG_TAG.code,
  PCP_REFERRAL_MISSING_ESS_TAG.code,
  PCP_REFERRAL_FINISHED_TAG.code,
];

export function getPatientSystemStatus(patient: Patient): PatientSystemStatus {
  const tags = patient.meta?.tag ?? [];

  const hasActiveTag = tags.some(
    (tag) => tag.system === POLARIS_SYSTEM_STATUS_TAG_SYSTEM && tag.code === PATIENT_SYSTEM_STATUS.active
  );
  if (patient.active || hasActiveTag) {
    return PATIENT_SYSTEM_STATUS.active;
  }

  const isProspect = tags.some((tag) => tag.system === TAG_SYSTEM && INTAKE_FORM_PHASES.includes(tag.code ?? ''));
  if (isProspect) {
    return PATIENT_SYSTEM_STATUS.prospect;
  }

  const isArchived = tags.some(
    (tag) => tag.system === POLARIS_SYSTEM_STATUS_TAG_SYSTEM && tag.code === PATIENT_SYSTEM_STATUS.archived
  );
  if (isArchived) {
    return PATIENT_SYSTEM_STATUS.archived;
  }

  return PATIENT_SYSTEM_STATUS.inactive;
}
